import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { isSpaPackageUnavailable } from '@features/packages/utils/isSpaPackageUnavailable';
import { entityHasAvailability } from '@features/spa/pages/SpaDay/configGroupBookability';
import {
  ConfigurationGroupType,
  SpaPackageListingType,
  SpaPackageType,
} from 'shared-types/SpaPackage';
import {
  BulkSpaPackageConfigGroups,
  BulkSpaPackageConfigGroupsEntry,
  useGetBulkSpaPackageConfigGroups,
} from './useGetBulkSpaPackageConfigGroups';

export const SPA_LISTING_BULK_AVAILABILITY_FLAG = 'feature.spa-listing-bulk-availability';

const DEFAULT_NUMBER_OF_GUESTS = 1;

const isEntry = (value: unknown): value is BulkSpaPackageConfigGroupsEntry =>
  typeof value === 'object' && value !== null;

const readConfigurationGroups = (
  entry: BulkSpaPackageConfigGroupsEntry
): ConfigurationGroupType[] =>
  Array.isArray(entry.configurationGroups) ? entry.configurationGroups : [];

export const isBulkResponseUnusable = (bulkConfigGroups: BulkSpaPackageConfigGroups): boolean => {
  const entries = Object.values(bulkConfigGroups ?? {}).filter(isEntry);

  return entries.length === 0 || entries.every((entry) => entry.error === true);
};

export const isEntryUnbookable = (
  entry: BulkSpaPackageConfigGroupsEntry,
  numberOfGuests: number
): boolean => {
  if (entry.hasAvailability === false) return true;

  const configurationGroups = readConfigurationGroups(entry);
  if (configurationGroups.length === 0) return false;

  return (
    !entityHasAvailability({ configurationGroups } as SpaPackageType) ||
    isSpaPackageUnavailable(configurationGroups, numberOfGuests)
  );
};

export const applyBulkBookability = <T extends SpaPackageListingType>(
  entity: T,
  bulkConfigGroups: BulkSpaPackageConfigGroups,
  numberOfGuests: number,
  canTrustAbsence: boolean
): T => {
  const entry = bulkConfigGroups[String(entity.id)];

  if (!isEntry(entry)) {
    return canTrustAbsence ? { ...entity, unAvailable: true } : entity;
  }

  if (entry.error === true) return entity;

  return isEntryUnbookable(entry, numberOfGuests) ? { ...entity, unAvailable: true } : entity;
};

export type SpaListingBulkAvailability<T> = {
  packages: T[];
  isFetching: boolean;
};

export function useSpaListingBulkAvailability<T extends SpaPackageListingType>(
  propertyId: number,
  language: string,
  date: string,
  guests: number | undefined,
  packages: T[],
  isPackageListCurrent = true
): SpaListingBulkAvailability<T> {
  const isEnabled = useFeatureIsOn(SPA_LISTING_BULK_AVAILABILITY_FLAG);
  const numberOfGuests = guests ?? DEFAULT_NUMBER_OF_GUESTS;

  const {
    data: bulkConfigGroups,
    isFetching,
    isError,
  } = useGetBulkSpaPackageConfigGroups(
    propertyId,
    language,
    date,
    numberOfGuests,
    packages.map((entity) => entity.id),
    isEnabled && isPackageListCurrent
  );

  const isVerdictPending =
    isEnabled && packages.length > 0 && !isError && (isFetching || !bulkConfigGroups);

  if (!isEnabled || !bulkConfigGroups) return { packages, isFetching: isVerdictPending };
  if (isBulkResponseUnusable(bulkConfigGroups)) {
    return { packages, isFetching: isVerdictPending };
  }

  const canTrustAbsence = guests !== undefined;

  return {
    packages: packages.map((entity) =>
      applyBulkBookability(entity, bulkConfigGroups, numberOfGuests, canTrustAbsence)
    ),
    isFetching: isVerdictPending,
  };
}
